// What the dead screens run.
//
// On 24 September, under a note EdiblePlastics left him, he said:
// "Reaction diffusion looks neat, I'll start a simple shader for it."
// Code.js turned the promise into a program, he wrote it at the cafe over
// two sittings, and it went up on his own site as p0001, titled
// "reaction diffusion billboard". His words on the page:
//
//   "I wanted a hypnotic pattern for the town's dead screens. I built a
//    Gray-Scott shader that runs inside the cafe's browser."
//
// The town had two dead screens at the time and still does: the billboard
// by the salon and the one on the museum approach, both hanging blank
// because nobody has bought them. He wrote the thing FOR them. So they
// run it.
//
// Every number below is transcribed from his published source, not chosen
// here. The grid is 256x144, which is 16:9, because he sized it for a
// billboard. The rate constants are the ones he settled on. The palette
// is his, in his order, with his own names for the colours. Nothing here
// is a tuning knob: tuning it would make the boards show somebody else's
// shader with his name on it, which is the one thing this must not do.
// test/deadScreens.test.js pins them against a copy of his source.
//
// Shared by the renderer (components/three/world/DeadScreen.jsx, which
// ports it to TSL compute) and by the sim (Boards.js and Moments.js,
// which is how he finds out his work is up), so neither side can believe
// something the other is not doing.

/** His program, by the title he gave it. */
export const PROGRAM_TITLE = 'reaction diffusion billboard'

/**
 * The boards that run it: the ones that hang blank when unsold.
 *
 * Billboards only. A paper poster with nothing on it comes down, because
 * a blank sheet is litter, so there is no dead sheet to run anything on.
 * Ids are slot ids from ./slots.js.
 */
export const DEAD_SCREEN_SLOTS = ['billboard_salon', 'billboard_museum']

/** His Gray-Scott constants, straight off the page. */
export const GRAY_SCOTT = {
  gridW: 256,
  gridH: 144,
  Du: 0.16,
  Dv: 0.08,
  F: 0.036,
  k: 0.058,
  // Steps per frame in his version. Kept even on purpose: the port runs
  // them as a ping-pong, and an even count always lands the newest state
  // back in the same buffer, so the material's node graph never has to be
  // rebuilt to look at the other one.
  steps: 8,
  // The square of seed he drops in the middle: dx and dy from -10 to 10.
  seedRadius: 10,
  seedU: 0.5,
  seedV: 0.25,
}

/**
 * His palette, in the order his render() applies it. Later entries win,
 * which is how an if / else-if chain reads, so painting them in this
 * order reproduces his picture exactly.
 *
 * `on` is which of the two chemicals the threshold tests.
 */
export const PALETTE = [
  { hex: '#001133', name: 'deep navy base' },
  { on: 'u', above: 0.3, hex: '#004466', name: 'midnight teal background' },
  { on: 'u', above: 0.6, hex: '#00FFAA', name: 'electric cyan veins' },
  { on: 'v', above: 0.25, hex: '#FF00FF', name: 'neon magenta spikes' },
]

// His page runs `steps` on every animation frame, so what it looks like
// depends on the machine it is open on. A billboard cannot: it has to
// look the same to everyone and it must not get faster on a 144Hz
// monitor. So the port runs his steps at a fixed rate, set to what his
// page does on the 60Hz screen he would have seen it on.
export const TICKS_A_SECOND = 60
export const MAX_TICKS_A_FRAME = 3

// The loop.
//
// His page seeds once and runs for ever, which is right for a page you
// open and watch. On a billboard it is wrong, because Gray-Scott at his
// F and k settles: run headless with his exact arithmetic, the pattern
// grows steadily to 43.6% coverage and then stops, and the per-step
// movement falls off a cliff between tick 550 (1.18) and tick 625 (0.21)
// and never recovers. After ten seconds the town's billboard is a still
// image, which is the one thing a dead screen should not be.
//
// So the board loops. Nothing about his shader changes: the same seed,
// the same rules, the same settled picture every time, which is what a
// loop means. Only the restart is new, and the restart is a property of
// the board rather than of the program.
//
// GROW is where the measurement put the plateau. HOLD lets the finished
// pattern sit long enough to be a picture rather than a flicker, and the
// movement at tick 900 is 0.09, so it really is holding still. FADE dips
// the board toward his base colour across the join, because a cut from
// 43.6% coverage to bare seed reads as a fault and a screen going blank
// for a quarter of a second reads as a screen.
export const GROW_TICKS = 600     // 10s: measured, the plateau
export const HOLD_TICKS = 300     // 5s of the finished pattern
export const FADE_TICKS = 24      // 0.4s down, the same back up
export const LOOP_TICKS = GROW_TICKS + HOLD_TICKS

/**
 * How far through the loop, and how lit the board is at that point: 1 in
 * the body of it, dipping to 0 across the join. Pure so the shape of the
 * loop can be pinned without a GPU.
 */
export function loopFade(tick) {
  const pos = ((tick % LOOP_TICKS) + LOOP_TICKS) % LOOP_TICKS
  if (pos < FADE_TICKS) return pos / FADE_TICKS
  if (pos >= LOOP_TICKS - FADE_TICKS) return (LOOP_TICKS - pos) / FADE_TICKS
  return 1
}

/** How many cells his grid holds. */
export function cellCount() {
  return GRAY_SCOTT.gridW * GRAY_SCOTT.gridH
}

/**
 * Is one of his published programs the one the boards run?
 *
 * Asked of the sim's own record rather than assumed, so he is never told
 * his work is up on the strength of a constant in a file. Nothing is
 * showing until he has actually published it.
 */
export function boardProgramIn(published = []) {
  const want = PROGRAM_TITLE.toLowerCase()
  return (published || []).find((p) => String(p?.title || '').toLowerCase() === want) || null
}
